// usePwa.ts
import { readonly, ref } from 'vue'
import { registerSW } from 'virtual:pwa-register'
import { apiEmployeeSubscriptionsPost } from '@/api'
import { useTitropEvents } from '@/composables/useTitropEvents.ts'
import { useLogger } from '@/composables/useLogger.ts'
import type { Pausable } from '@vueuse/shared'

const logger = useLogger('PWA')
const UPDATE_AVAILABLE = ref(false)
const IS_OFFLINE_READY = ref(false)

const updateServiceWorker = ref<undefined | ((reloadPage?: boolean) => Promise<void>)>()

const updateCheckerInterval = ref<Pausable>()
useTitropEvents().onLoginSuccess.once(() => {
  if ('serviceWorker' in navigator) {
    console.log('[PWA] Installing service worker')
    updateServiceWorker.value = registerSW({
      // immediate: true,
      onRegisterError: (_error: Error) => {
        //debugger
      },
      onNeedRefresh() {
        UPDATE_AVAILABLE.value = true
        logger.info('onNeedRefresh')
        window.location.reload() // new
      },
      onOfflineReady() {
        IS_OFFLINE_READY.value = true
        logger.info('onOfflineReady')
      },
      onRegisteredSW(swUrl, registration) {
        logger.info('[PWA] onRegisteredSW')
        if (registration?.waiting) {
          logger.info('[PWA] waiting')
          registration.waiting.postMessage({ type: 'SKIP_WAITING' })
        }

        if (!updateCheckerInterval.value) {
          logger.info('[PWA] updateCheckerInterval started')
          updateCheckerInterval.value = useIntervalFn(() => {
            if (updateServiceWorker.value) {
              logger.info('[PWA] updateCheckerInterval checking...')
              void updateServiceWorker.value()
            }
          }, 5000)
        }

        registration?.update() //new
      }
    })

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('[App] New SW controlling → reloading...')
      console.log('!! New service worker: enabled?')
      window.location.reload() //new
    })
  } else {
    logger.error('[PWA] Browser does not support service worker')
  }
})

export function usePwa() {
  const isReloadPending = readonly(UPDATE_AVAILABLE)
  const isOfflineReady = readonly(IS_OFFLINE_READY)

  // const confirmUpdate = async () => {
  //   if (updateServiceWorker.value) await updateServiceWorker(true)
  // }

  const registerPush = async () => {
    if (!('serviceWorker' in navigator)) {
      logger.error('[PWA-Push] Browser does not support push')
      return
    }
    const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
      const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
      const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
      const rawData = atob(base64)
      return new Uint8Array([...rawData].map((c) => c.charCodeAt(0)))
    }

    const registration = await navigator.serviceWorker.ready
    const existing = await registration.pushManager.getSubscription()
    if (existing) return

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(__VAPID_PUBLIC_KEY__)
    })
    const p256dh = subscription.getKey('p256dh')
    const auth = subscription.getKey('auth')
    if (!p256dh || !auth) return

    try {
      await apiEmployeeSubscriptionsPost<true>({
        body: {
          endpoint: subscription.endpoint,
          auth: btoa(String.fromCharCode(...new Uint8Array(auth))),
          p256dh: btoa(String.fromCharCode(...new Uint8Array(p256dh)))
          //       auth: this.arrayBufferToBase64url(auth),
          //       p256dh: this.arrayBufferToBase64url(p256dh)
        }
      })
    } catch (_e) {
    }
  }

  return {
    isReloadPending: isReloadPending,
    isOfflineReady,
    // confirmUpdate,
    registerPush
  }
}
